import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileText, Brain, Calendar, User, Settings, AlertTriangle } from 'lucide-react';
import { problemDiagnosisService } from '../../services/problemDiagnosisService';
import UserInputTab from './UserInputTab';
import AIReportsTab from './AIReportsTab';
import { DiagnosisDetailOptimizedProps } from '../../types/components';
import { 
  DiagnosisSession, 
  DiagnosisReport, 
  UserAnswer, 
  DiagnosisDetailTab,
  EditState,
  UpdateSessionContentRequest
} from '../../types/problemDiagnosis';
import toast from 'react-hot-toast';

const DiagnosisDetailOptimized: React.FC<DiagnosisDetailOptimizedProps> = ({
  sessionId,
  onBack
}) => {
  const [session, setSession] = useState<DiagnosisSession | null>(null);
  const [reports, setReports] = useState<DiagnosisReport[]>([]);
  const [answers, setAnswers] = useState<UserAnswer[]>([]);
  const [activeTab, setActiveTab] = useState<DiagnosisDetailTab>('input');
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [editState, setEditState] = useState<EditState>({
    isEditing: false,
    editingField: null,
    hasUnsavedChanges: false,
    autoSaveEnabled: true
  });

  useEffect(() => {
    fetchSessionDetail();
  }, [sessionId]);

  const fetchSessionDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('正在获取会话详情，sessionId:', sessionId);

      const response = await problemDiagnosisService.getHistory({
        session_id: sessionId
      });

      console.log('获取到的响应数据:', response);

      if (response.sessions && response.sessions.length > 0) {
        const sessionData = response.sessions[0];
        console.log('会话数据:', sessionData);
        
        setSession(sessionData);
        setReports(sessionData.diagnosis_reports || []);
        setAnswers(sessionData.answers || []);
        
        // 自动选择最新的报告
        if (sessionData.diagnosis_reports && sessionData.diagnosis_reports.length > 0) {
          const latestReport = sessionData.diagnosis_reports.find(r => r.is_latest) || 
                              sessionData.diagnosis_reports[0];
          setSelectedReportId(latestReport.id);
        }
      } else {
        setError('未找到会话数据');
      }
    } catch (error) {
      console.error('获取诊断详情失败:', error);
      setError('获取诊断详情失败');
      toast.error('获取诊断详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = async (tab: DiagnosisDetailTab) => {
    // 如果有未保存的更改，提示用户
    if (editState.hasUnsavedChanges) {
      const confirmed = window.confirm('您有未保存的更改，确定要切换标签页吗？未保存的更改将丢失。');
      if (!confirmed) {
        return;
      }
      
      // 重置编辑状态
      setEditState({
        isEditing: false,
        editingField: null,
        hasUnsavedChanges: false,
        autoSaveEnabled: true
      });
    }
    
    setActiveTab(tab);
  };

  const handleEditToggle = (editing: boolean, field?: 'problem_description' | 'answers') => {
    setEditState({
      isEditing: editing,
      editingField: field || null,
      hasUnsavedChanges: false,
      autoSaveEnabled: true
    });
  };

  const handleSave = async (data: UpdateSessionContentRequest) => {
    if (!session) return;

    try {
      setSaving(true);
      
      // 这里应该调用更新会话内容的API
      // 暂时模拟更新本地状态
      const updatedSession = {
        ...session,
        problem_description: data.problem_description || session.problem_description,
        updated_at: new Date().toISOString()
      };
      
      setSession(updatedSession);
      
      if (data.answers) {
        setAnswers(data.answers);
      }
      
      setEditState({
        isEditing: false,
        editingField: null,
        hasUnsavedChanges: false,
        autoSaveEnabled: true
      });
      
      toast.success('保存成功');
    } catch (error) {
      console.error('保存失败:', error);
      toast.error('保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDraftSave = (fieldName: string, value: any) => {
    // 实现草稿自动保存逻辑
    console.log('自动保存草稿:', fieldName, value);
    // 这里可以调用草稿保存API
  };

  const handleRegenerateReport = async () => {
    if (!session || !answers.length) {
      toast.error('请先完成问题描述和五大原理分析');
      return;
    }

    try {
      setRegenerating(true);
      
      const report = await problemDiagnosisService.generateReport({
        session_id: sessionId,
        problem_description: session.problem_description,
        selected_system: session.selected_system!,
        answers: answers.map(answer => ({
          principle_type: answer.principle_type,
          question_text: answer.question_text,
          user_answer: answer.user_answer,
          answer_order: answer.answer_order
        }))
      });

      console.log('重新生成的报告:', report);

      // 更新报告列表
      const updatedReports = [...reports, report];
      setReports(updatedReports);
      setSelectedReportId(report.id);
      
      // 切换到报告标签页
      setActiveTab('reports');
      
      toast.success('AI诊断报告生成成功！');
    } catch (error) {
      console.error('重新生成报告失败:', error);
      toast.error('生成报告失败，请稍后重试');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      // 这里应该调用删除报告的API
      const updatedReports = reports.filter(r => r.id !== reportId);
      setReports(updatedReports);
      
      // 如果删除的是当前选中的报告，选择其他报告
      if (selectedReportId === reportId) {
        const nextReport = updatedReports.find(r => r.is_latest) || updatedReports[0];
        setSelectedReportId(nextReport?.id || null);
      }
      
      toast.success('报告删除成功');
    } catch (error) {
      console.error('删除报告失败:', error);
      toast.error('删除报告失败');
    }
  };

  const getSystemName = (systemType: string) => {
    const systemNames = {
      machine: '机械系统',
      organism: '生理系统',
      social: '社会系统',
      ecosystem: '生态系统'
    };
    return systemNames[systemType as keyof typeof systemNames] || systemType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {error || '诊断记录不存在'}
        </h3>
        <p className="text-gray-600 mb-4">
          {error || '该诊断记录可能已被删除或不存在'}
        </p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          返回历史记录
        </button>
      </div>
    );
  }

  const hasAnswers = answers.some(answer => answer.user_answer.trim());

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            返回历史记录
          </button>
          <div className="text-sm text-gray-500">
            <Calendar className="w-4 h-4 inline mr-1" />
            {new Date(session.created_at).toLocaleString('zh-CN')}
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">诊断详情</h1>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center">
            <Settings className="w-4 h-4 mr-1" />
            {getSystemName(session.selected_system!)}
          </div>
          <div className="flex items-center">
            <User className="w-4 h-4 mr-1" />
            状态：{session.status === 'completed' ? '已完成' : session.status === 'active' ? '进行中' : '已放弃'}
          </div>
        </div>
      </div>

      {/* 标签页导航 */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => handleTabChange('input')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'input'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              用户录入
              {editState.hasUnsavedChanges && (
                <span className="ml-1 w-2 h-2 bg-orange-500 rounded-full inline-block"></span>
              )}
            </button>
            
            <button
              onClick={() => handleTabChange('reports')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Brain className="w-4 h-4 inline mr-2" />
              AI诊断报告
              {reports.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                  {reports.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* 未保存更改警告 */}
        {editState.hasUnsavedChanges && (
          <div className="px-6 py-3 bg-orange-50 border-b border-orange-200">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <span className="text-sm text-orange-800">
                您有未保存的更改，切换标签页前请先保存
              </span>
            </div>
          </div>
        )}

        {/* 标签页内容 */}
        <div className="p-6">
          {activeTab === 'input' ? (
            <UserInputTab
              session={session}
              answers={answers}
              editState={editState}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
              onRegenerateReport={handleRegenerateReport}
              onDraftSave={handleDraftSave}
              isRegenerating={regenerating}
            />
          ) : (
            <AIReportsTab
              reports={reports}
              selectedReportId={selectedReportId}
              onReportSelect={setSelectedReportId}
              onDeleteReport={handleDeleteReport}
              onRegenerateReport={handleRegenerateReport}
              isRegenerating={regenerating}
              hasAnswers={hasAnswers}
            />
          )}
        </div>
      </div>

      {/* 保存状态指示器 */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>保存中...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosisDetailOptimized;